import { useEffect } from 'react';

import { useDatasetSearch, PAGE_SIZE } from '~modules/search/hooks/use-dataset-search';
import { useSearchState } from '~modules/search/hooks/use-search-state';

import { Typography } from '~ui/base/typography';

import { SearchEmpty } from './search-empty';
import { SearchError } from './search-error';
import { SearchPagination } from './search-pagination';
import { SearchResultCard } from './search-result-card';
import { SearchSkeleton } from './search-skeleton';

interface SearchResultsProps {
  query: string;
}

function SearchResults({ query }: SearchResultsProps) {
  const { page, setPage, reset } = useSearchState();
  const { data, isLoading, isFetching, isError, error, refetch } =
    useDatasetSearch({ query, page });

  // Smoothly scroll to the top of the results region on a page change so
  // visitors don't have to scroll back up themselves.
  useEffect(() => {
    if (page > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page]);

  if (isLoading) {
    return <SearchSkeleton count={3} />;
  }

  if (isError) {
    const message = error instanceof Error ? error.message : undefined;
    return <SearchError message={message} onRetry={() => refetch()} />;
  }

  if (!data) {
    return null;
  }

  if (data.data.length === 0) {
    return <SearchEmpty query={query} onClear={reset} />;
  }

  return (
    <section
      className="flex flex-col gap-y-6"
      aria-busy={isFetching}
      aria-live="polite"
    >
      <Typography variant="caption-md" as="p" className="text-muted-foreground">
        {data.total_count.toLocaleString()} {data.total_count === 1 ? 'dataset' : 'datasets'}
        {' '}{data.total_count === 1 ? 'matches' : 'match'}{' '}
        <span className="text-foreground">“{query}”</span>
      </Typography>

      <ul className="flex flex-col gap-y-4">
        {data.data.map((dataset) => (
          <li key={dataset.doi}>
            <SearchResultCard dataset={dataset} />
          </li>
        ))}
      </ul>

      <SearchPagination
        page={page}
        totalCount={data.total_count}
        pageSize={PAGE_SIZE}
        hasMore={data.has_more}
        onPageChange={setPage}
      />
    </section>
  );
}

export { SearchResults };
